import React, { useEffect, useState } from 'react';

import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select/SelectInput';
import { useMutation, useQuery } from '@tanstack/react-query';

import { Dropdown, DropdownOption, IDropdown } from '~/components/Dropdown';
import { ArrowIcon } from '~/components/icons';
import { DisplayJiraStatus } from '~/components/JiraStatus';
import { Retry } from '~/components/Retry';
import { useNotification } from '~/contexts/NotificationContext';
import { getTransitions, updateTransition } from '~/controllers/issueTransitions';
import { EHttpStatus } from '~/domain/EHttpStatus';
import { IJiraStatus } from '~/domain/IJiraStatus';
import { IJiraTransition } from '~/domain/IJiraTransition';
import { ResponseError } from '~/domain/ResponseError';
import { useTrigger } from '~/hooks/useTrigger';
import { CACHE_TIME_1HR, STALE_TIME_1HR } from '~/queryCache';

interface IUpdateJiraStatus extends Omit<IDropdown, 'ref'> {
  issueId?: string;
  value?: IJiraStatus;
  notificationLabel: string;
  typeName: string;
  onChange?: () => void;
  format?: (status: IJiraStatus) => React.ReactNode;
  filter?: (transition: IJiraTransition) => boolean;
}

export const UpdateJiraStatus: React.FC<IUpdateJiraStatus> = (props) => {
  const { issueId, value, typeName, notificationLabel, disabled, onChange, format, filter, ...others } = props;
  const [internalValue, setInternalValue] = useState<IJiraStatus | undefined>(value);
  const [enabledQuery, queryHook] = useTrigger();
  const notify = useNotification();

  const {
    isLoading: isSaving,
    isSuccess,
    mutate,
    reset,
    isError: isSavingError,
    error,
  } = useMutation(updateTransition.bind(undefined, issueId));

  const {
    data: transitions,
    isFetching,
    isError: isTransitionsError,
    refetch,
  } = useQuery(
    ['getTransitions', { status: internalValue?.id, typeName }],
    getTransitions.bind(undefined, issueId),
    {
      enabled: enabledQuery && !isSaving && !!internalValue,
      cacheTime: CACHE_TIME_1HR,
      staleTime: STALE_TIME_1HR,
      select: (data) => {
        return data ? data.transitions : [];
      },
    });

  const isBadRequest = (error as ResponseError)?.status === EHttpStatus.BadRequest;

  const options = transitions?.filter(x => (x.to.id !== internalValue?.id) && (!filter || filter(x)))?.map((transition) => {
    const { id, name, to } = transition;
    return (
      <DropdownOption key={id} value={id} sx={{ display: isTransitionsError ? 'none' : undefined }}>
        {to.name === name ? 'Transition to' : name}
        <Box component="span" sx={{ display: 'flex', alignItems: 'center', flex: 1, justifyContent: 'flex-end' }}>
          <ArrowIcon sx={{ width: '15px', height: '13px', mx: '8px' }} />
          <DisplayJiraStatus status={to} format={format} />
        </Box>
      </DropdownOption>
    );
  });

  const handleStatusChange = (e: SelectChangeEvent) => {
    const transition = transitions?.find(x => x.id === e.target.value);
    if (transition) {
      queryHook.deactivate();
      setInternalValue(transition.to);
      mutate(transition);
    }
  };

  const handleErrorDialogClose = () => {
    reset();
    setInternalValue(value);
  }

  useEffect(() => {
    if (isSuccess) {
      reset();
      notify({
        type: 'success',
        message: `${notificationLabel} ${issueId} status has been set to ${internalValue?.name}`,
      });
      if (onChange) {
        onChange();
      }
    }
  }, [isSuccess, issueId, notify, onChange, reset, internalValue, notificationLabel]);

  useEffect(() => {
    setInternalValue(value);
  }, [value]);

  return (
    <>
      <Dropdown
        name="status"
        defaultValue=""
        value=""
        disabled={disabled || isSaving}
        isLoading={isFetching || !internalValue}
        onOpen={queryHook.activate}
        onChange={handleStatusChange}
        customRenderValue
        displayEmpty
        renderValue={() => {
          return internalValue && format ? format(internalValue) : internalValue?.name || 'Loading...';
        }}
        {...others}
      >
        {options}
        {options?.length === 0 && !isFetching && internalValue &&
          <DropdownOption key={internalValue.id} value={internalValue.id} sx={{ display: isTransitionsError ? 'none' : undefined }}>
            No change
            <Box component="span" sx={{ display: 'flex', alignItems: 'center', flex: 1, justifyContent: 'flex-end' }}>
              <ArrowIcon sx={{ width: '15px', height: '13px', mx: '8px' }} />
              <DisplayJiraStatus status={internalValue} format={format} />
            </Box>
          </DropdownOption>
        }
        {isTransitionsError && !isFetching && <Retry retry={refetch} sx={{ p: '30px' }} variant="h5">the status</Retry>}
      </Dropdown>
      <Dialog
        maxWidth="xs"
        open={isSavingError && !isBadRequest}
        onClose={(_, reason) => {
          reason === 'escapeKeyDown' && handleErrorDialogClose();
        }}
      >
        <DialogTitle>
          Sorry, something went wrong, the changes are not saved.
        </DialogTitle>
        <DialogActions sx={{ justifyContent: 'center' }}>
          <Button color="dark" onClick={handleErrorDialogClose}>Okay</Button>
        </DialogActions>
      </Dialog>
      <Dialog
        maxWidth="xs"
        open={isBadRequest}
        onClose={(_, reason) => {
          reason === 'escapeKeyDown' && handleErrorDialogClose();
        }}
      >
        <DialogTitle sx={{ textAlign: 'center' }}>
          Cannot Complete This Action
        </DialogTitle>
        <DialogContent sx={{ textAlign: 'center' }}>
          We cannot complete this action because the request might have been updated. Please refresh page to stay in sync.
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center' }}>
          <Button color="secondary" onClick={handleErrorDialogClose}>
            Cancel
          </Button>
          <Button
            color="dark"
            onClick={() => {
              handleErrorDialogClose();
              onChange && onChange();
            }}
          >
            Refresh
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
